import { useEffect, useState } from "react";
import {
  assignWorkOrderToFreelancer,
  deleteOffer,
  fetchWorkOrderOffers,
  routeOffer,
  unrouteOffers,
  updateWorkOrderStatus,
} from "../../../services/api/offers";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";
import BaseButton from "../../../components/ui/BaseButton";
import { Avatar } from "@mui/material";
import BaseRating from "../../../components/ui/BaseRating";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import BaseSelect from "../../../components/ui/BaseSelect";
import ReviewCounterOfferModal from "../ReviewCounterOfferModal";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import { CurrencySymbol, PaymentType } from "../../../utils/enum";
import { sortByPaymentType } from "../../../utils/sortByPaymentType";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { fetchNearbyRateComparison } from "../../../services/api/user";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { setWorkOrderDetail } from "../../../store/workOrder/workOrderSlice";
import OpenableLink from "../../../components/ui/OpenableLink";
import BulkMessageModal from "./BulkMessageModal";
import { formatRating } from "../../../utils/helpers";

const freelanceCompanyOf = (company) =>
  company && company.id !== Number(process.env.REACT_APP_TECHDOMAIN_COMPANY_ID) ? company : null;

const getOfferCompany = (item) =>
  freelanceCompanyOf(item?.createdBy?.ownedCompany) || freelanceCompanyOf(item?.user?.ownedCompany);

const PendingAssignment = ({
  filters,
  radius,
  setRadius,
  refetchProvidersCount,
  workOrderStatus,
  workOrderSubStatus,
  workOrderPaymentType,
}) => {
  const navigate = useNavigate();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const [reviewCounterOfferModalOpen, setReviewCounterOfferModalOpen] = useState(false);
  const [freelancerId, setFreelancerId] = useState(null);
  const [offerId, setOfferId] = useState(null);
  const [routeUserId, setRouteUserId] = useState(null);
  const [loadingButton, setLoadingButton] = useState({ type: null, id: null });
  const [assigningUserId, setAssigningUserId] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [filterByRequiredSkills, setFilterByRequiredSkills] = useState(false);
  const [filterByRequiredTools, setFilterByRequiredTools] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [isBulkUnrouting, setIsBulkUnrouting] = useState(false);

  const [warningModal, setWarningModal] = useState({
    open: false,
    minRate: null,
    offerId: null,
    isAssigning: false,
    currency: null,
    type: "confirm",
  });
  const [confirmAction, setConfirmAction] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    field: "",
    order: "",
  });

  const { id } = useParams();
  const dispatch = useDispatch();
  const workOrderLocation = useSelector((state) => state.workOrder.workOrderDetails.address);

  const { updateWorkOrder } = useUpdateWorkOrder();

  const {
    isLoading: isTechniciansLoading,
    error: isTechniciansError,
    data: engineersData,
    refetch: refetchWorkOrderOffers,
  } = useQuery({
    queryKey: [
      "filteredFreelancers",
      filters,
      queryParams.page,
      queryParams.limit,
      queryParams.field,
      queryParams.order,
      filterByRequiredSkills,
      filterByRequiredTools,
    ],
    queryFn: () =>
      fetchWorkOrderOffers({
        id,
        filters: filters,
        limit: queryParams?.limit,
        page: queryParams?.page,
        ...(queryParams.field && queryParams.order
          ? { sortBy: queryParams.field, sortOrder: queryParams.order }
          : {}),
        filterByRequiredSkills,
        filterByRequiredTools,
      }),
  });
  const {
    isPending: isRouteLoading,
    error: isRouteError,
    mutateAsync: routeOfferMutation,
  } = useMutation({
    mutationFn: routeOffer,
    onSuccess: (data) => {
      dispatch(
        setWorkOrderDetail({
          ...workOrderDetail,
          offersCount: {
            ...workOrderDetail.offersCount,
            routed: workOrderDetail.offersCount.routed + 1,
          },
        }),
      );
      dispatch(
        showToast({
          message: "Job Routed Successfully",
          type: "success",
        }),
      );
    },
  });

  const {
    isPending: isOfferDeleteLoading,
    error: isOfferDeleteError,
    mutateAsync: deleteOfferMutation,
  } = useMutation({
    mutationFn: deleteOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "UnRouted Successfully",
          type: "success",
        }),
      );

      updateWorkOrderDetailPageUI();
    },
  });

  const { mutateAsync: unrouteOffersBulkMutation } = useMutation({
    mutationFn: unrouteOffers,
  });

  const {
    isPending: isAssignLoading,
    status,
    error: isAssignWorkOrderError,
    mutateAsync: assignWorkOrderToFreelancerMutation,
  } = useMutation({
    mutationFn: assignWorkOrderToFreelancer,
  });

  const {
    isPending: isUpdateWorkOrderLoading,
    // status,
    error: isupdateWorkOrderStatusError,
    mutateAsync: updateWorkOrderStatusMutation,
  } = useMutation({
    mutationFn: updateWorkOrderStatus,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Job Updated Successfully",
          type: "success",
        }),
      );
      updateWorkOrderDetailPageUI();
    },
  });
  const {
    mutateAsync: fetchNearbyRateComparisonMutation,
    isPending: isRateComparisonLoading,
    error: rateComparisonError,
  } = useMutation({
    mutationFn: fetchNearbyRateComparison,
  });
  const getRateByPaymentType = (data, paymentType) => {
    switch (paymentType) {
      case "HOURLY":
        return data?.hourlyRate;
      case "HALF_DAY":
        return data?.halfDayRate;
      case "FULL_DAY":
        return data?.fullDayRate;
      case "FIXED":
        return data?.fixedAmount;
      default:
        return null;
    }
  };
  async function handleRouteButton(userId) {
    try {
      setRouteUserId(userId);
      let payload = { workOrderId: workOrderDetail.id, userId };
      const routedResponse = await routeOfferMutation(payload);
      refetchWorkOrderOffers();
      refetchProvidersCount();
      updateWorkOrder();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setRouteUserId(null);
    }
  }
  const handleAssignWorkOrderWithRateCheck = async (selectedOffer, onConfirmAction) => {
    try {
      const offerData = selectedOffer?.isCounter
        ? selectedOffer?.counterOfferData
        : selectedOffer?.requested;

      if (!offerData) return;

      const currency = selectedOffer?.isCounter ? offerData?.engineerCurrency : offerData?.currency;
      const paymentType = offerData?.paymentType;
      const userRate = getRateByPaymentType(offerData, paymentType);

      if (!userRate) {
        dispatch(
          showToast({
            message: "Cannot Assign: Rates must be greater than zero ",
            type: "error",
          }),
        );
        return;
      }

      const rateComparisonData = await fetchNearbyRateComparisonMutation({
        distance: 150,
        paymentType,
        latitude: workOrderLocation?.latitude,
        longitude: workOrderLocation?.longitude,
        currency,
      });

      const minRate = rateComparisonData?.minRate;
      setSelectedOffer(selectedOffer);
      setConfirmAction(() => onConfirmAction);

      setWarningModal({
        open: true,
        minRate: minRate ?? null,
        type: minRate && userRate > minRate ? "warning" : "confirm",
      });
    } catch (error) {
      console.error(error);
    }
  };

  async function handleAssignWorkOrderButton(userId, workOrderId) {
    try {
      setLoadingButton({ type: "assign", id: userId });
      let payload = { userId, workOrderId };
      const result = await assignWorkOrderToFreelancerMutation(payload);
      updateWorkOrderDetailPageUI();
      setWarningModal((prev) => ({ ...prev, open: false }));
      setReviewCounterOfferModalOpen(false);
      dispatch(
        showToast({
          message: "Job Assign Successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      // Clear it, not re-set it: the start of this handler sets the same value,
      // so re-setting left the row's assign button spinning forever on failure.
      setLoadingButton({ type: null, id: null });
    }
  }

  async function handleUpdateWorkOrderStatusButton(
    userId,
    workOrderId,
    status = "PENDING",
    offerId,
  ) {
    try {
      setLoadingButton({ type: "unassign", id: userId });
      let payload = { userId, workOrderId, status, ...(offerId && { offerId }) };
      await updateWorkOrderStatusMutation(payload);
      updateWorkOrderDetailPageUI();
    } catch (error) {
      dispatch(
        showToast({
          message: isAssignWorkOrderError?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setLoadingButton({ type: null, id: null });
    }
  }

  const data = engineersData?.data?.offers || [];

  const updateWorkOrderDetailPageUI = () => {
    refetchWorkOrderOffers();
    updateWorkOrder();
    refetchProvidersCount();
  };

  const handleReviewCounterOfferButton = (freelancerId, offerId, offer = null) => {
    setSelectedOffer(offer);
    setFreelancerId(freelancerId);
    setOfferId(offerId);
    setReviewCounterOfferModalOpen(true);
  };

  const unRouteButtonClicked = async (id) => {
    try {
      setLoadingButton({ type: "unroute", id });
      const deleteOfferResponse = await deleteOfferMutation(id);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setLoadingButton({ type: "unroute", id: null });
    }
  };

  const renderHeaderCell = (label) => {
    if (!filters.includes("PENDING")) return null; // Only in Pending tab
    return (
      <div className="bg-[#EFF4FF] w-full h-full flex items-center justify-start font-semibold text-gray-500">
        {label || ""}
      </div>
    );
  };

  let groupedData = [];
  if (filters.includes("PENDING")) {
    const requestedOffers = data.filter(
      (item) => !item.isCounter && !["ROUTED", "ACCEPTED"].includes(item.status),
    );

    const counteredOffers = data.filter(
      (item) => item.isCounter && !["ROUTED", "ACCEPTED"].includes(item.status),
    );

    const routedOffers = data.filter((item) => ["ROUTED", "ACCEPTED"].includes(item.status));

    groupedData = [
      ...(requestedOffers.length
        ? [
            {
              id: "header-requested",
              type: "header",
              label: "REQUESTED OFFERS",
            },
          ]
        : []),
      ...requestedOffers.map((item) => ({ ...item, type: "row" })),

      ...(counteredOffers.length
        ? [{ id: "header-countered", type: "header", label: "COUNTER OFFERS" }]
        : []),
      ...counteredOffers.map((item) => ({ ...item, type: "row" })),

      ...(routedOffers.length
        ? [{ id: "header-routed", type: "header", label: "ROUTED OFFERS" }]
        : []),
      ...routedOffers.map((item) => ({ ...item, type: "row" })),
    ];
  } else {
    groupedData = data.map((item) => ({ ...item, type: "row" }));
  }

  const COLUMNS = [
    {
      headerName: "Action",
      field: "action",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const item = params?.row;
        if (params?.row?.type === "header") return renderHeaderCell(params.row.label);

        if (!item?.user) return null;

        // For a freelance company offer, createdBy is the company owner and user is the
        // referred engineer. Messaging should open a chat with the company (owner) only —
        // not the engineer. Mirrors the companyOwnerId logic used in the Name column.
        const messageFreelanceCompany = freelanceCompanyOf(item?.createdBy?.ownedCompany);
        const messageTargetId = messageFreelanceCompany ? item?.createdBy?.id : item?.user?.id;

        return (
          <div className="flex items-center overflow-auto h-10">
            {(item?.status === "PENDING" || item?.status === "ACCEPTED") && (
              <BaseButton
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                className="bg-white hover:bg-primary hover:text-white text-primary mr-3 w-[70px]"
                onClick={() =>
                  handleUpdateWorkOrderStatusButton(
                    item.user.id,
                    item.workOrderId,
                    "REJECTED",
                    item.id,
                  )
                }
              >
                Hide
              </BaseButton>
            )}
            {!item?.isCounter && item?.status === "ACCEPTED" && workOrderStatus === "PUBLISHED" && (
              <BaseButton
                loaderSize={18}
                loaderColor="primary"
                isLoading={loadingButton.type === "assign" && loadingButton.id === item?.user?.id}
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                onClick={() => handleReviewCounterOfferButton(item.user.id, item.workOrderId, item)}
                className="bg-white hover:bg-primary hover:text-white text-primary ml-0.5 mr-3 w-[80px]"
              >
                Review
              </BaseButton>
            )}
            {!item?.isCounter && item?.status === "PENDING" && workOrderStatus === "PUBLISHED" && (
              <BaseButton
                loaderSize={18}
                loaderColor="primary"
                isLoading={loadingButton.type === "assign" && loadingButton.id === item?.user?.id}
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                onClick={() => handleReviewCounterOfferButton(item.user.id, item.workOrderId, item)}
                className="bg-white hover:bg-primary hover:text-white text-primary ml-0.5 mr-3 w-[80px]"
              >
                Review
              </BaseButton>
            )}
            {workOrderStatus === "PUBLISHED" && item?.isCounter && item?.status === "PENDING" && (
              <BaseButton
                onClick={() => handleReviewCounterOfferButton(item.user.id, item.id, item)}
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                className="bg-white hover:bg-primary hover:text-white text-primary mr-3 w-[100px]"
              >
                Review
              </BaseButton>
            )}

            {filters.includes("ASSIGNED") &&
              workOrderStatus === "ASSIGNED" &&
              workOrderSubStatus === "NOT_CONFIRMED" && (
                <BaseButton
                  loaderSize={18}
                  loaderColor="primary"
                  isLoading={loadingButton.type === "unassign" && loadingButton.id === item.user.id}
                  style={{
                    boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                    paddingTop: "0px",
                    paddingBottom: "0px",
                  }}
                  onClick={() =>
                    handleUpdateWorkOrderStatusButton(
                      item.user.id,
                      item.workOrderId,
                      "PENDING",
                      item.id,
                    )
                  }
                  className="bg-white hover:bg-primary hover:text-white text-primary mr-3 w-[102px]"
                >
                  un-Assign
                </BaseButton>
              )}
            {filters.includes("DECLINED") && workOrderStatus === "PUBLISHED" && (
              <BaseButton
                loaderSize={18}
                loaderColor="primary"
                isLoading={routeUserId === params.row?.user?.id}
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                className="bg-white hover:bg-primary hover:text-white text-primary mr-3 ml-0.5 w-[70px]"
                onClick={() => handleRouteButton(params?.row?.user?.id)}
              >
                Route
              </BaseButton>
            )}

            {item?.status === "ROUTED" && (
              <BaseButton
                loaderSize={18}
                loaderColor="primary"
                isLoading={loadingButton.type === "unroute" && loadingButton.id === item?.id}
                style={{
                  boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
                className="bg-white hover:bg-primary hover:text-white text-primary mr-3 w-[100px]"
                onClick={() => unRouteButtonClicked(item.id)}
              >
                un-route
              </BaseButton>
            )}
            <BaseButton
              onClick={() =>
                navigate(`/workOrder/messages/${item.workOrderId}?engineerId=${messageTargetId}`)
              }
              style={{
                boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
              className="bg-white hover:bg-primary hover:text-white text-primary mr-3 w-[100px]"
            >
              Message
            </BaseButton>
          </div>
        );
      },
    },
    {
      field: "id",
      headerName: "ID",
      sortable: true,
      flex: 0.5,
      renderCell: (params) => {
        const item = params?.row;
        if (item?.type === "header") return renderHeaderCell("");

        const engineerId = item?.user?.id || item?.id;
        const displayCompany = getOfferCompany(item);
        if (!displayCompany) return <p className="text-primary">{engineerId}</p>;

        const showEngineerId = item?.status !== "ROUTED";
        return (
          <div>
            <p className="text-primary font-medium">{displayCompany.id}</p>
            {showEngineerId && <p className="text-xs text-gray-500">{engineerId}</p>}
          </div>
        );
      },
    },
    {
      field: "engineer",
      headerName: "Name",
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const item = params?.row;
        if (item.type === "header") {
          return renderHeaderCell("");
        }

        const freelanceCompany = freelanceCompanyOf(item?.createdBy?.ownedCompany);
        const displayCompany = getOfferCompany(item);
        const hasReferredEngineer = freelanceCompany && item.user.id !== item.createdBy.id;
        const memberOffer =
          !hasReferredEngineer && freelanceCompany
            ? data.find(
                (o) =>
                  o.createdBy?.id === item.user.id &&
                  o.user?.id !== item.user.id &&
                  o.status === "ASSIGNED",
              )
            : null;
        const showCompanyRow = hasReferredEngineer || !!memberOffer;

        if (item.status === "ROUTED" && displayCompany) {
          const companyOwnerId = freelanceCompany ? item?.createdBy?.id : item?.user?.id;
          return (
            <div className="flex items-center gap-2">
              <Avatar sx={{ bgcolor: "#2561A92A", width: 30, height: 30 }}>
                <i className="fa-solid fa-building text-primary text-xs"></i>
              </Avatar>
              <div>
                <OpenableLink
                  as="p"
                  to={`/user/details/profile/${companyOwnerId}`}
                  className="text-primary font-medium capitalize cursor-pointer hover:underline"
                >
                  {displayCompany.name}
                </OpenableLink>
                <p className="text-xs text-gray-500">Pending engineer...</p>
              </div>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-2">
            {displayCompany ? (
              <Avatar sx={{ bgcolor: "#2561A92A", width: 30, height: 30 }}>
                <i className="fa-solid fa-building text-primary text-xs"></i>
              </Avatar>
            ) : (
              <Avatar src={item?.user?.profilePicture} sx={{ width: 30, height: 30 }} />
            )}
            <div>
              {showCompanyRow ? (
                <>
                  <OpenableLink
                    as="p"
                    to={`/user/details/profile/${item?.createdBy?.id}`}
                    className="text-primary font-medium cursor-pointer hover:underline"
                  >
                    {freelanceCompany.name}
                  </OpenableLink>
                  <OpenableLink
                    as="p"
                    to={`/user/details/profile/${item?.user?.id}?direct=true`}
                    className="text-xs text-gray-500 capitalize cursor-pointer hover:underline"
                  >
                    {item?.user?.firstName} {item?.user?.lastName}
                  </OpenableLink>
                </>
              ) : (
                <OpenableLink
                  as="div"
                  to={`/user/details/profile/${item?.user?.id}`}
                  className="cursor-pointer"
                >
                  <p className={`text-primary capitalize ${displayCompany ? "font-medium" : ""}`}>
                    {displayCompany
                      ? displayCompany.name
                      : `${item?.user?.firstName} ${item?.user?.lastName}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {displayCompany
                      ? `${item?.user?.firstName} ${item?.user?.lastName}`
                      : item?.user?.phoneNumber}
                  </p>
                </OpenableLink>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: "billingRates",
      sortable: false,
      headerName: "Payment Rates",
      width: 250,
      renderCell: (params) => {
        const item = params.row;
        if (item.type === "header") return renderHeaderCell("");

        const sortedRates = sortByPaymentType(item?.user?.billingRates);
        if (!sortedRates?.length) {
          return <p className="table-font-size">-</p>;
        }
        return (
          <div className="flex flex-wrap justify-between table-font-size w-full">
            {sortedRates?.map((rate, i) => (
              <div key={i}>
                <p className="h-[14px] text-primary text-center text-[11px] font-medium">
                  {`${rate.rate} ${CurrencySymbol[rate.currency]}` || ""}
                </p>
                <p className="text-center">{PaymentType[rate.paymentType]}</p>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "talentPools",
      headerName: "Talent Pool",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        if (params?.row?.type === "header") return renderHeaderCell("");
        const pools = params?.row?.user?.talentPools || [];
        return (
          <div className="flex flex-wrap font-normal table-font-size">
            {pools.map((p, index) => (
              <span key={p.id}>
                <OpenableLink
                  to={`/workForce/talentPool/engineers/${p.id}`}
                  className="text-primary cursor-pointer hover:underline"
                >
                  {p.name}
                </OpenableLink>
                {index < pools.length - 1 && ", "}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      field: "privateFeedbackCount",
      headerName: "Private Feedback",
      sortable: false,
      flex: 0.5,
      renderCell: (item) => {
        if (item?.row?.type === "header") return renderHeaderCell("");
        return <p>{item?.row?.user?.privateFeedbackCount}</p>;
      },
    },
    {
      field: "userSkills",
      headerName: "Skills",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        const skills =
          params.row?.user?.userSkills?.map((skill) => skill.subCategory.subCategory) || [];
        const skillsText = skills.join(", ");

        return (
          <BaseTooltip
            position="bottom"
            interactive={true}
            content={
              <div className="relative pb-2">
                <p className="text-white">{skillsText}</p>
              </div>
            }
            appendTo={document.body}
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {skillsText}
            </p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "averageRating",
      headerName: "Ratings",
      sortable: false,
      flex: 0.5,
      renderCell: (item) => {
        if (item?.row?.type === "header") return renderHeaderCell("");
        return (
          <div className="flex items-center">
            <BaseRating rating={item?.row?.user?.averageRating} />
            <p className="ml-3">{formatRating(item?.row?.user?.averageRating)}</p>
          </div>
        );
      },
    },
    {
      headerName: "Jobs",
      field: "workedWithJobsCount",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        // if (params?.row?.type === "header") return null;
        if (params?.row?.type === "header") {
          return renderHeaderCell("");
        }
        return (
          <div className="table-font-size">
            {params.row.user?.workedWith ? (
              <p className="text-primary font-medium">
                {params.row.user?.workedWithJobsCount}{" "}
                <span className="text-[11px] font-300">(My Company)</span>
              </p>
            ) : (
              <p className="font-medium">
                {params.row.user?.marketplaceJobsCount}
                <span className="text-[11px] font-300"> (Marketplace)</span>
              </p>
            )}
          </div>
        );
      },
    },
    {
      headerName: "Distance",
      field: "distance",
      flex: 0.5,
      sortable: true,
      renderCell: (item) => {
        if (item?.row?.type === "header") return renderHeaderCell("");
        const distance = item?.row?.user?.distance ?? 0;
        return (
          <div className="flex items-center">
            <i className="fa-light fa-car text-xs"></i>
            <p className="text-primary ml-2">{distance?.toFixed(2)} km</p>
          </div>
        );
      },
    },
    {
      field: "cancellationRate",
      headerName: "Cancelation",
      sortable: false,
      flex: 0.5,
      renderCell: (params) =>
        params?.row?.type === "header" ? (
          renderHeaderCell("")
        ) : (
          <div className="flex items-center">
            <p className="ml-1">{params.row.user.cancellationRate} %</p>
          </div>
        ),
    },
    {
      field: "timelinessRate",
      headerName: "Timeliness",
      sortable: false,
      flex: 0.5,
      renderCell: (params) =>
        params?.row?.type === "header" ? (
          renderHeaderCell("")
        ) : (
          <div className="flex items-center">
            <p className="ml-1">{params.row.user.timelinessRate} %</p>
          </div>
        ),
    },
  ];

  const handleRadiusChange = (e) => {
    setRadius(e.target.value);
  };
  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel?.page + 1,
      limit: newPaginationModel?.pageSize,
    }));
  };
  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      setQueryParams((prev) => ({
        ...prev,
        field: newSortModel[0].field,
        order: newSortModel[0].sort,
      }));
    }
  };

  async function handleBulkUnroute() {
    const routedOffers = selectedOffers.filter((o) => o.status === "ROUTED");
    if (!routedOffers.length) return;
    setIsBulkUnrouting(true);
    try {
      // One request for the whole selection: the backend unroutes all of them or none.
      await unrouteOffersBulkMutation({ offerIds: routedOffers.map((offer) => offer.id) });
      dispatch(
        setWorkOrderDetail({
          ...workOrderDetail,
          offersCount: {
            ...workOrderDetail.offersCount,
            routed: Math.max(0, workOrderDetail.offersCount.routed - routedOffers.length),
          },
        }),
      );
      dispatch(
        showToast({
          message: `${routedOffers.length} offer${routedOffers.length !== 1 ? "s" : ""} unrouted successfully`,
          type: "success",
        }),
      );
      setSelectedRows([]);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to unroute the selected offers",
          type: "error",
        }),
      );
    } finally {
      updateWorkOrderDetailPageUI();
      setIsBulkUnrouting(false);
    }
  }

  const handleSelectionChange = (newSelection) => {
    const filtered = newSelection.filter((id) => !String(id).startsWith("header-"));
    setSelectedRows(filtered);
  };

  const selectedOffers = groupedData.filter(
    (item) => item.type === "row" && selectedRows.includes(item.id),
  );

  return (
    <>
      <div className="mt-1 mb-3 grid grid-cols-12 items-center">
        <div className="col-span-12 lg:col-span-8 flex flex-wrap items-center gap-4">
          <div className="flex items-center md:mt-0 mt-4">
            <p className="mr-2">Providers:</p>
            <p className="ml-2 bg-[#EFF4FF] px-5 py-1 rounded-md">
              {engineersData?.data?.totalCount || 0}
            </p>
          </div>
          <div className="flex items-center md:mt-0 mt-4 ml-2">
            <BaseCheckbox
              label="Filter by Skills"
              id="userSkills"
              name="userSkills"
              checked={filterByRequiredSkills}
              onChange={(e) => setFilterByRequiredSkills(e.target.checked)}
            />
          </div>
          <div className="flex items-center md:mt-0 mt-4 ml-2">
            <BaseCheckbox
              label="Filter by Tools"
              id="userTools"
              name="userTools"
              checked={filterByRequiredTools}
              onChange={(e) => setFilterByRequiredTools(e.target.checked)}
            />
          </div>
          {selectedOffers.length > 0 && selectedOffers.every((o) => o.status === "ROUTED") && (
            <BaseButton
              isLoading={isBulkUnrouting}
              loaderSize={18}
              loaderColor="white"
              onClick={handleBulkUnroute}
              style={{
                boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
              className="bg-white text-primary border border-primary hover:bg-primary hover:text-white"
            >
              Unroute Selected ({selectedOffers.length})
            </BaseButton>
          )}
          {selectedRows.length > 0 && (
            <BaseButton
              onClick={() => setShowBulkMessageModal(true)}
              style={{
                boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.15)",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
              className="bg-primary text-white"
            >
              Message Selected ({selectedRows.length})
            </BaseButton>
          )}
        </div>
      </div>
      <BaseDataTableV2
        className="max-h-[calc(100vh-300px)] overflow-auto"
        getRowHeight={(params) => (params?.model?.type === "header" ? 30 : 45)}
        getRowClassName={(params) => (params?.row?.type === "header" ? "header-row" : "")}
        columns={COLUMNS}
        data={groupedData || []}
        isLoading={isTechniciansLoading}
        disableColumnResize={false}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        disableColumnSorting={false}
        hideFooter={false}
        pagination
        paginationMode="server"
        rowCount={engineersData?.data?.totalCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        sortingMode="server"
        sortModel={
          queryParams.field && queryParams.order
            ? [{ field: queryParams.field, sort: queryParams.order }]
            : []
        }
        onSortModelChange={handleSortModelChange}
        sortingOrder={["asc", "desc"]}
      />

      {reviewCounterOfferModalOpen && selectedOffer && (
        <ReviewCounterOfferModal
          reviewCounterOfferModalOpen={reviewCounterOfferModalOpen}
          setReviewCounterOfferModalOpen={setReviewCounterOfferModalOpen}
          selectedOffer={selectedOffer}
          freelancerId={freelancerId}
          offerId={offerId}
          updateWorkOrderDetailPageUI={updateWorkOrderDetailPageUI}
          handleUpdateWorkOrderStatusButton={handleUpdateWorkOrderStatusButton}
          handleAssignWorkOrderWithRateCheck={handleAssignWorkOrderWithRateCheck}
          handleAssignWorkOrderButton={handleAssignWorkOrderButton}
          isRateComparisonLoading={isRateComparisonLoading}
        />
      )}
      {showBulkMessageModal && selectedOffers.length > 0 && (
        <BulkMessageModal
          open={showBulkMessageModal}
          onClose={() => {
            setShowBulkMessageModal(false);
            setSelectedRows([]);
          }}
          selectedOffers={selectedOffers}
          workOrderDetail={workOrderDetail}
        />
      )}
      {warningModal.open && (
        <BaseConfirmModal
          open={warningModal.open}
          onClose={() => setWarningModal((prev) => ({ ...prev, open: false }))}
          onConfirm={async () => {
            if (confirmAction) {
              await confirmAction();
            }
            setWarningModal((prev) => ({ ...prev, open: false }));
          }}
          isLoading={isAssignLoading}
          title={
            warningModal.type === "warning"
              ? "Are you sure you want to assign this work order?"
              : "Confirm Assignment"
          }
          description={
            warningModal.type === "warning" ? (
              <p>A lower rate engineer exists in this region.</p>
            ) : (
              <p>Are you sure you want to assign this work order?</p>
            )
          }
          confirmText="Yes, Assign"
          cancelText="Cancel"
          type={warningModal.type === "warning" && "warning"}
        />
      )}
    </>
  );
};

export default PendingAssignment;
